import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { ArrowLeft, Bell, Download, Filter, Loader2, RefreshCw, CheckCircle2, XCircle, Mail, Smartphone } from 'lucide-react';
import Pagination from '../../components/common/Pagination';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// A neat label for our known event_type values. Falls back to the raw key.
const EVENT_LABELS = {
  live_class_rescheduled: 'Class rescheduled',
  live_class_cancelled: 'Class cancelled',
  live_class_host_assigned: 'Host assigned',
  live_class_archive_published: 'Class recap',
  live_class_1h_reminder: '1h reminder',
  live_class_host_1h: 'Host 1h reminder',
  live_class_15m_reminder: '15m reminder',
  live_class_live_now: 'Live now',
  new_classes_daily_digest: 'Daily digest',
  class_recap: 'Class recap (email)',
  welcome: 'Welcome',
  payment_success: 'Payment success',
  subscription_expiring: 'Subscription expiring',
  subscription_expired: 'Subscription expired',
  password_changed: 'Password changed',
  password_reset_by_admin: 'Password reset by admin',
  new_device_login: 'New device login',
  '2fa_changed': '2FA changed',
  admin_broadcast: 'Admin broadcast',
  community_ready: 'Community ready',
};

const eventLabel = (t) => EVENT_LABELS[t] || t;

const NotificationLogsPage = () => {
  const navigate = useNavigate();

  // Filters
  const [email, setEmail] = useState('');
  const [userId, setUserId] = useState('');
  const [eventType, setEventType] = useState('all');
  const [channel, setChannel] = useState('all');
  const [delivered, setDelivered] = useState('all');
  const [classId, setClassId] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);

  // Data
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [deliveredCount, setDeliveredCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [deliveryRate, setDeliveryRate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [facets, setFacets] = useState({ event_types: [], channels: [] });

  const buildParams = useCallback(() => {
    const p = {};
    if (email.trim()) p.email = email.trim();
    if (userId.trim()) p.user_id = userId.trim();
    if (eventType && eventType !== 'all') p.event_type = eventType;
    if (channel && channel !== 'all') p.channel = channel;
    if (delivered !== 'all') p.delivered = delivered === 'yes' ? 'true' : 'false';
    if (classId.trim()) p.class_id = classId.trim();
    if (start) p.start = new Date(start).toISOString();
    if (end) p.end = new Date(end).toISOString();
    return p;
  }, [email, userId, eventType, channel, delivered, classId, start, end]);

  const fetchFacets = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/notification-logs/facets`);
      setFacets(data);
    } catch (e) {
      // Not fatal — filters just won't be populated. Log so ops can see it
      // in the browser console if a user reports empty filter dropdowns.
      console.warn('Failed to load notification-log facets:', e);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...buildParams(), page, per_page: perPage };
      const { data } = await axios.get(`${API}/admin/notification-logs`, { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setDeliveredCount(data.delivered || 0);
      setFailedCount(data.failed || 0);
      setDeliveryRate(data.delivery_rate);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load logs');
    } finally {
      setLoading(false);
    }
  }, [buildParams, page, perPage]);

  useEffect(() => {
    fetchFacets();
  }, [fetchFacets]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const resetFilters = () => {
    setEmail('');
    setUserId('');
    setEventType('all');
    setChannel('all');
    setDelivered('all');
    setClassId('');
    setStart('');
    setEnd('');
    setPage(1);
  };

  const applyFilters = () => {
    setPage(1);
    fetchLogs();
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = buildParams();
      const res = await axios.get(`${API}/admin/notification-logs/export.csv`, {
        params,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.download = `notification_log_${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Layout>
      <div className="page-transition">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={() => navigate('/admin/tools')} className="text-gray-600" data-testid="notif-logs-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Admin Tools
          </Button>
        </div>

        <Card className="mb-6 border-l-4 border-l-[#6366F1] shadow-lg">
          <CardHeader className="bg-gradient-to-r from-[#6366F1]/5 to-[#0A7EA4]/5">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-[#6366F1] rounded-lg">
                <Bell className="w-7 h-7 text-white" />
              </div>
              <div>
                <CardTitle className="text-2xl text-[#6366F1]">Notification Log</CardTitle>
                <CardDescription className="text-sm mt-1">
                  Every email and in-app notification the platform fires. Rolls 90 days.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Filters */}
        <Card className="mb-4">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Filter className="w-4 h-4" /> Filters
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs text-gray-600">Recipient email (partial)</Label>
                <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@example.com" data-testid="notif-logs-email-filter" />
              </div>
              <div>
                <Label className="text-xs text-gray-600">User ID (exact)</Label>
                <Input value={userId} onChange={(e) => setUserId(e.target.value)} placeholder="uuid…" data-testid="notif-logs-userid-filter" />
              </div>
              <div>
                <Label className="text-xs text-gray-600">Event type</Label>
                <Select value={eventType} onValueChange={setEventType}>
                  <SelectTrigger data-testid="notif-logs-eventtype-filter"><SelectValue placeholder="All events" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All events</SelectItem>
                    {facets.event_types.map((t) => (
                      <SelectItem key={t} value={t}>{eventLabel(t)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-gray-600">Channel</Label>
                <Select value={channel} onValueChange={setChannel}>
                  <SelectTrigger data-testid="notif-logs-channel-filter"><SelectValue placeholder="All channels" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All channels</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="in_app">In-app</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-gray-600">Delivered</Label>
                <Select value={delivered} onValueChange={setDelivered}>
                  <SelectTrigger data-testid="notif-logs-delivered-filter"><SelectValue placeholder="Any status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any status</SelectItem>
                    <SelectItem value="yes">Delivered</SelectItem>
                    <SelectItem value="no">Failed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-gray-600">Class ID</Label>
                <Input value={classId} onChange={(e) => setClassId(e.target.value)} placeholder="class uuid" data-testid="notif-logs-classid-filter" />
              </div>
              <div>
                <Label className="text-xs text-gray-600">From</Label>
                <Input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} data-testid="notif-logs-start-filter" />
              </div>
              <div>
                <Label className="text-xs text-gray-600">To</Label>
                <Input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="notif-logs-end-filter" />
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              <Button onClick={applyFilters} disabled={loading} data-testid="notif-logs-apply-btn">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Filter className="w-4 h-4 mr-2" />}
                Apply
              </Button>
              <Button variant="outline" onClick={resetFilters} data-testid="notif-logs-reset-btn">Reset</Button>
              <Button variant="outline" onClick={() => { setPage(1); fetchLogs(); }} data-testid="notif-logs-refresh-btn">
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh
              </Button>
              <div className="ml-auto">
                <Button variant="outline" onClick={exportCsv} disabled={exporting || total === 0} data-testid="notif-logs-export-btn">
                  {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  Export CSV
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-gray-500 uppercase">Total in filter</p>
              <p className="text-2xl font-semibold text-gray-900">{total.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-gray-500 uppercase">Delivered</p>
              <p className="text-2xl font-semibold text-emerald-600">{deliveredCount.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-gray-500 uppercase">Failed</p>
              <p className={`text-2xl font-semibold ${failedCount ? 'text-red-600' : 'text-gray-400'}`}>{failedCount.toLocaleString()}</p>
              {deliveryRate !== null && (
                <p className="text-xs text-gray-500 mt-1">Delivery rate: {(deliveryRate * 100).toFixed(1)}%</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Log table */}
        <Card>
          <CardContent className="p-0">
            {loading && items.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Loading…
              </div>
            ) : items.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Bell className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                No log entries match the current filter.
              </div>
            ) : (
              <div className="overflow-x-auto" data-testid="notif-logs-table">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">When</th>
                      <th className="text-left px-3 py-2 font-medium">Channel</th>
                      <th className="text-left px-3 py-2 font-medium">Event</th>
                      <th className="text-left px-3 py-2 font-medium">Recipient</th>
                      <th className="text-left px-3 py-2 font-medium">Subject</th>
                      <th className="text-left px-3 py-2 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((r) => {
                      const dt = new Date(r.created_at);
                      const chan = r.channel === 'email' ? Mail : Smartphone;
                      const ChanIcon = chan;
                      const recipient = r.to_email || r.resolved_email || r.user_id || '—';
                      const recipientName = r.resolved_name;
                      return (
                        <tr key={r.id} className="border-t hover:bg-gray-50" data-testid={`notif-logs-row-${r.id}`}>
                          <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                            {isNaN(dt) ? r.created_at : dt.toLocaleString()}
                          </td>
                          <td className="px-3 py-2">
                            <span className="inline-flex items-center gap-1 text-xs">
                              <ChanIcon className="w-3.5 h-3.5 text-gray-500" />
                              {r.channel === 'email' ? 'Email' : 'In-app'}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            <Badge variant="outline" className="text-xs">{eventLabel(r.event_type)}</Badge>
                            {r.class_id && (
                              <div className="text-[10px] text-gray-400 mt-0.5 truncate max-w-[160px]" title={r.class_id}>class:{r.class_id.slice(0, 8)}…</div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-gray-700">
                            <div className="truncate max-w-[220px]" title={recipient}>{recipient}</div>
                            {recipientName && <div className="text-[10px] text-gray-400">{recipientName}</div>}
                          </td>
                          <td className="px-3 py-2 text-gray-700">
                            <div className="truncate max-w-[280px]" title={r.subject || ''}>{r.subject || '—'}</div>
                          </td>
                          <td className="px-3 py-2">
                            {r.delivered ? (
                              <span className="inline-flex items-center gap-1 text-emerald-600 text-xs">
                                <CheckCircle2 className="w-3.5 h-3.5" /> Delivered
                              </span>
                            ) : (
                              <div>
                                <span className="inline-flex items-center gap-1 text-red-600 text-xs">
                                  <XCircle className="w-3.5 h-3.5" /> Failed
                                </span>
                                {r.error && (
                                  <div className="text-[10px] text-red-500 mt-0.5 max-w-[220px] truncate" title={r.error}>{r.error}</div>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {total > 0 && (
              <div className="px-4 py-3 border-t bg-gray-50">
                <Pagination
                  currentPage={page}
                  totalItems={total}
                  itemsPerPage={perPage}
                  onPageChange={setPage}
                  onItemsPerPageChange={(n) => { setPerPage(n); setPage(1); }}
                />
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default NotificationLogsPage;
